import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Settings2, Save, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/PageHeader";
import { Field } from "@/components/common/FormShell";
import { UserName } from "@/components/common/EntityName";
import { settingsApi } from "@/lib/api";
import { fmtRelativeTime } from "@/lib/utils";

/**
 * The app's public settings — contact details, links and legal text — as one
 * form. Keys are the ones the mobile app reads from GET /api/v1/settings.
 */
const SECTIONS: { title: string; description: string; fields: { key: string; label: string; long?: boolean; placeholder?: string }[] }[] = [
  {
    title: "Contact & links",
    description: "Shown on the app's Help & Support screen.",
    fields: [
      { key: "appName", label: "App name", placeholder: "Afya24" },
      { key: "supportEmail", label: "Support email", placeholder: "info@afya24.co.tz" },
      { key: "supportPhone", label: "Support phone", placeholder: "+255 …" },
      { key: "whatsappNumber", label: "WhatsApp number", placeholder: "+255 …" },
      { key: "address", label: "Address", placeholder: "Dar es Salaam, Tanzania" },
      { key: "website", label: "Website", placeholder: "https://afya24.co.tz" },
      { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/…" },
      { key: "twitter", label: "X / Twitter", placeholder: "https://x.com/…" },
      { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/…" },
    ],
  },
  {
    title: "About & legal",
    description: "Plain text; the app shows it as written.",
    fields: [
      { key: "about", label: "About Afya24", long: true },
      { key: "privacyPolicy", label: "Privacy policy", long: true },
      { key: "termsAndConditions", label: "Terms & conditions", long: true },
    ],
  },
];

export default function SettingsPage() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["settings"], queryFn: () => settingsApi.all().then((r) => r.data.data ?? []) });
  const [values, setValues] = useState<Record<string, string>>({});
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (settings.data) setValues(Object.fromEntries(settings.data.map((s) => [s.key, s.value ?? ""])));
  }, [settings.data]);

  const dirty = useMemo(() => {
    const original = Object.fromEntries((settings.data ?? []).map((s) => [s.key, s.value ?? ""]));
    return Object.keys(values).some((k) => (values[k] ?? "") !== (original[k] ?? ""));
  }, [values, settings.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await settingsApi.save(values);
      if (!res.data.data) throw new Error(res.data.errors?.[0]?.message ?? `Save refused (${res.data.code}).`);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); setSavedAt(Date.now()); },
  });

  const lastChange = (settings.data ?? [])
    .filter((s) => s.updatedAt)
    .sort((a, b) => (b.updatedAt! > a.updatedAt! ? 1 : -1))[0];

  return (
    <>
      <PageHeader
        title="App settings"
        subtitle={lastChange?.updatedAt
          ? <>Last change {fmtRelativeTime(lastChange.updatedAt)} by <UserName id={lastChange.updatedBy} />.</>
          : "Contact details, links and legal text shown in the app."}
        action={
          <div className="flex items-center gap-3">
            {savedAt && !dirty && (
              <span className="flex items-center gap-1 text-sm text-success"><CheckCircle2 className="size-4" /> Saved</span>
            )}
            {save.error && <span className="text-sm text-destructive">{(save.error as Error).message}</span>}
            <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              <Save className="size-4" /> {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        }
      />

      {SECTIONS.map((section) => (
        <Card key={section.title} className="mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Settings2 className="size-4 text-primary" /> {section.title}</CardTitle>
            <CardDescription>{section.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={section.fields.some((f) => f.long) ? "space-y-4" : "grid gap-4 md:grid-cols-2"}>
              {section.fields.map((f) => (
                <Field key={f.key} label={f.label}>
                  {f.long ? (
                    <textarea
                      value={values[f.key] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      rows={8}
                      className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  ) : (
                    <Input
                      value={values[f.key] ?? ""}
                      placeholder={f.placeholder}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    />
                  )}
                </Field>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}
